import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import MarketingNav from '../components/layout/MarketingNav';
import { Button, Card, Input } from '../components/ui';

const EyeIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
  </svg>
);

const EyeOffIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
  </svg>
);

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(formData.username, formData.password);

    if (result.success) {
      // Redirect based on user role
      if (result.user?.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } else {
      setError(result.message);
    }

    setLoading(false);
  };

  return (
    <>
      <MarketingNav transparent={false} />
      <main className="flex min-h-screen items-center justify-center bg-neutral-50 px-6 py-12 mt-20">
      <div className="w-full max-w-md">
        <Card>
          <div className="text-center">
            <p className="text-sm font-semibold uppercase tracking-wide text-primary-600">MHDF Momentum</p>
            <h1 className="mt-2 text-3xl font-display font-semibold text-neutral-900">Welcome Back</h1>
            <p className="mt-2 text-neutral-500">Sign in to continue your mental wellness journey.</p>
          </div>

          {error && (
            <div className="mt-4 rounded-soft border border-error-200 bg-error-50 p-3 text-sm text-error-700">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            <Input
              label="Username"
              name="username"
              type="text"
              autoComplete="username"
              value={formData.username}
              onChange={handleChange}
              fullWidth
              required
            />
            <Input
              label="Password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              value={formData.password}
              onChange={handleChange}
              endIcon={showPassword ? EyeOffIcon : EyeIcon}
              onEndIconClick={() => setShowPassword(!showPassword)}
              fullWidth
              required
            />

            <div className="flex items-center justify-between text-sm">
              <Link to="/forgot-password" className="font-semibold text-primary-600 hover:text-primary-700">
                Forgot password?
              </Link>
              <Link to="/forgot-username" className="font-semibold text-primary-600 hover:text-primary-700">
                Forgot username?
              </Link>
            </div>

            <Button type="submit" fullWidth disabled={loading} className="w-full">
              {loading ? 'Signing In...' : 'Sign In'}
            </Button>
          </form>

          <p className="mt-4 text-center text-sm text-neutral-500">
            Don't have an account?{' '}
            <Link to="/register" className="font-semibold text-primary-600 hover:text-primary-700">
              Sign Up
            </Link>
          </p>
        </Card>
      </div>
    </main>
    </>
  );
};

export default Login;
